import { AddonGroupType, SubItemType } from "../../types";
import {
  getFranchiseCustomPricesHelper,
  getHighestCustomPriceHelper,
} from "./pricing";

const priceAddonGroupsForFranchise = (
  groups: AddonGroupType[],
  franchiseId: number,
  includeNested: boolean
): AddonGroupType[] =>
  groups.map((group) =>
    // A menu listing carries group headers without options; leave those as
    // they are, `hasHydratedAddonGroups` tells them apart by the missing array.
    group.addonGroupSubItem
      ? {
          ...group,
          addonGroupSubItem: group.addonGroupSubItem.map((option) => ({
            ...option,
            price: getFranchiseCustomPricesHelper(
              franchiseId,
              option.basePrice ?? option.price,
              option.customFranchiseAddonGroupSubItemPrices
            ),
            ...(includeNested &&
              option.subItem?.allAddonGroups && {
                subItem: {
                  ...option.subItem,
                  allAddonGroups: priceAddonGroupsForFranchise(
                    option.subItem.allAddonGroups,
                    franchiseId,
                    false
                  ),
                },
              }),
          })),
        }
      : group
  );

/**
 * A copy of `subItem` priced for one branch, by the same rules as
 * getFranchiseCustomPrices: the sub-item, its addon options and their nested
 * options.
 *
 * That one prices the menu in place; this one leaves its input alone, so it is
 * safe on frozen redux state, and raises no alert, since it runs in a selector
 * on every lookup change.
 */
export const priceSubItemForFranchise = (
  subItem: SubItemType,
  franchiseId: number
): SubItemType => {
  const basePrice = subItem.basePrice ?? subItem.price;
  const customPrices = subItem.customFranchiseSubItemPrices ?? [];

  return {
    ...subItem,
    price:
      franchiseId > 0
        ? getFranchiseCustomPricesHelper(franchiseId, basePrice, customPrices)
        : getHighestCustomPriceHelper(basePrice, customPrices),
    ...(subItem.allAddonGroups && {
      allAddonGroups: priceAddonGroupsForFranchise(
        subItem.allAddonGroups,
        franchiseId,
        true
      ),
    }),
  };
};
